import { useEffect, useState } from 'react';
import { getReportsSellsDates } from '../../../services/reports/sellsService';
import { Box, Card, EmptyState, HorizontalGrid, Page, Text } from '@shopify/polaris';
import { getSearchParams, handleSearchParams } from '../../../helpers/handleSearchParams';
import FiltersLikeSells from '../../UI/Reports/FiltersLikeSells';
import { hydrateFilters } from '../../../services/productService';
import { getDeposits } from '../../../services/depositsService';
import DensityChart from '../../UI/Graphs/DensityChart';
import { useTimezone } from '../../../customHooks/useTimezone';
import { displayInTz, parseFilterParam } from '../../../helpers/dates';

const defaultFilters = {
  genders: {
    name: 'Género',
    slug: 'genders',
    bundle: true,
  },
  categories: {
    name: 'Categorías',
    slug: 'categories',
    bundle: true,
  },
  subcategories: {
    name: 'Subcategorías',
    slug: 'subcategories',
    bundle: true,
  },
  deposits: {
    name: 'Depósitos',
    slug: 'deposits',
  },
  customers: {
    name: 'Clientes',
    slug: 'customers',
    value: '2',
  },
};
const setLabelValue = (inputArray) => inputArray.map((i) => ({ label: i.name, value: i.id }));

const ViewReportSellsGraph = () => {
  const { timezone } = useTimezone();

  const [defaultDates, setDefaultDates] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStock = (fltr) => {
    const validFilters = Object.keys(fltr)
      .map((k) => (fltr[k].value ? { slug: k, value: fltr[k].value } : null))
      .filter((f) => f)
      .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {});

    // groupTz ya no se envía: el back asume MX por defecto (negocio mexicano).
    return getReportsSellsDates({ ...validFilters }).then((r) => {
      setStock({
        ...r.data,
      });
    });
  };

  const handleFilter = ({ slug, value }) => setFilters({ ...filters, [slug]: { ...filters[slug], value } });
  const handleDateChange = (dates) => {
    Object.keys(dates).forEach((k) => handleSearchParams(k, dates[k]));

    setFilters({
      ...filters,
      since: {
        value: dates.since,
      },
      until: {
        value: dates.until,
      },
    });
  };
  const clearFilters = () => {
    const currentFilters = { ...filters };
    Object.keys(defaultFilters).forEach((k) => {
      currentFilters[k].value = null;
    });
    setFilters(currentFilters);
  };

  useEffect(() => {
    Promise.all([
      hydrateFilters(
        Object.keys(defaultFilters)
          .map((k) => defaultFilters[k])
          .reduce((acc, f) => ({ ...acc, [f.slug]: f }), {}),
      ),
      getDeposits({ limit: 1000, excludeGroups: true }),
    ]).then(([f, d]) => {
      setFilters({
        ...filters,
        ...f,
        deposits: {
          ...f.deposits,
          options: setLabelValue([...d.data.data]),
        },
      });

      const period = {
        since: parseFilterParam(getSearchParams('since'), timezone),
        until: parseFilterParam(getSearchParams('until'), timezone),
      };

      if (period.since && period.until) setDefaultDates(period);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Object.keys(filters).forEach((k) => handleSearchParams(k, filters[k].value));
    if (!filters.until || !filters.since) return;
    setLoading(true);
    !loading && loadStock(filters).then(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (!stock) return;
    console.log(stock);
  }, [stock]);

  return (
    <Page title="Stock">
      <Card roundedAbove="sm">
        <FiltersLikeSells
          filters={filters}
          handleDateChange={handleDateChange}
          handleFilter={handleFilter}
          defaultDates={defaultDates}
          clearFilters={clearFilters}
        />
      </Card>
      <StockWrapper stock={stock} filters={filters} onLoadStock={loadStock} />
      {stock && <DensityChart data={stock.data} />}
    </Page>
  );
};

const StockWrapper = ({ stock, filters, onLoadStock }) => {
  return (
    <>
      {stock && (
        <>
          <Box minHeight=".5rem" />
          {!stock.data.length && (
            <>
              <Card roundedAbove="sm">
                <EmptyState
                  heading="No hay datos para mostrar"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                ></EmptyState>
              </Card>
            </>
          )}

          {stock.data.length > 0 && (
            <>
              <Card roundedAbove="sm">
                <Numbers {...stock.metadata} stock={stock.data} />
                <Box minHeight=".5rem" />
              </Card>

              {/* <ProductsWrapper
                data={stock}
                TableComponent={(args) => <ProductStockTable {...args} />}
                loadPage={() => onLoadStock(stock.metadata.page + 1, filters)}
              /> */}
            </>
          )}
        </>
      )}
    </>
  );
};

const Numbers = ({ max, min, avg, stdev, stock }) => {
  const { timezone } = useTimezone();
  const numbers = [
    {
      label: 'Día de máxima venta',
      value: Intl.NumberFormat('es-Es').format(max) + ' Uds.',
      subvalue: displayInTz(stock.find((s) => s.qty === max)?.date, timezone),
    },
    {
      label: 'Día de mínima venta',
      value: Intl.NumberFormat('es-Es').format(min) + ' Uds.',
      subvalue: displayInTz(stock.find((s) => s.qty === min)?.date, timezone),
    },
    {
      label: 'Venta media diaria',
      value:
        Intl.NumberFormat('es-Es', {
          maximumFractionDigits: 2,
        }).format(avg) + ' Uds.',
    },
    {
      label: 'Desviación típica',
      value:
        Intl.NumberFormat('es-Es', {
          maximumFractionDigits: 2,
        }).format(stdev) + ' Uds.',
    },
  ];

  return (
    <HorizontalGrid columns={{ xs: 1, md: numbers.map(() => '1fr').join(' ') }} gap="4">
      {numbers.map((n) => (
        <Box key={n.label}>
          <Text variant="headingSm" as="h4">
            {n.label}
          </Text>
          <Box border="divider" borderRadius="base" minHeight=".5rem" />
          <Text variant="headingXl">{n.value}</Text>
          {n.subvalue && <Text variant="headingSm">{n.subvalue}</Text>}
        </Box>
      ))}
    </HorizontalGrid>
  );
};

export default ViewReportSellsGraph;
